import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { apiFetch } from "@/lib/api";
import { parseDraftComposePrefillLines, parseDraftComposeLocation } from "@/lib/purchaseOrderDraftForm";
import type { MrpLocation } from "@/lib/mrp";

/**
 * Legacy /mrp/purchase-orders/compose?… bookmarks: create a hub PO and redirect to numeric id.
 */
export function PurchaseOrderComposeLegacyRedirect() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const locationCode = parseDraftComposeLocation(searchParams.get("location_code")) ?? ("sgs" as MrpLocation);
      const supplierRaw = searchParams.get("supplier_id");
      const supplierId =
        supplierRaw != null && /^\d+$/.test(supplierRaw) ? Number.parseInt(supplierRaw, 10) : undefined;
      const linesParam = searchParams.get("lines");
      const prefillLines = parseDraftComposePrefillLines(linesParam, locationCode);
      const body: Record<string, unknown> = { location_code: locationCode };
      if (supplierId != null && supplierId > 0) {
        body.supplier_id = supplierId;
      }
      if (prefillLines != null && prefillLines.length > 0) {
        body.lines = prefillLines.map((line) => ({
          sku: line.sku,
          line_description: line.line_description,
          quantity: line.quantity,
          unit_price: line.unit_price,
          expected_delivery_date: line.expected_delivery_date,
          transfer_requests: line.transfer_requests,
        }));
      }
      try {
        const created = await apiFetch<{ id: number }>("/api/mrp/purchase-orders", {
          method: "POST",
          body,
        });
        if (!cancelled) {
          navigate(`/mrp/purchase-orders/${created.id}`, { replace: true });
        }
      } catch {
        if (!cancelled) {
          navigate("/mrp/purchase-orders", { replace: true });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate, searchParams]);

  return <p className="text-sm text-slate-600">Opening purchase order…</p>;
}

export async function createHubPurchaseOrderAndNavigate(
  navigate: (path: string, options?: { replace?: boolean }) => void,
  input: {
    location_code: MrpLocation;
    supplier_id?: number;
    lines?: Array<{
      sku: string;
      line_description?: string;
      quantity: string;
      unit_price?: string;
      expected_delivery_date?: string;
      transfer_requests?: Array<{
        quantity?: number | string;
        origin_location_id?: number;
        destination_location_id?: number;
        target_date?: string;
        reason?: string;
        notes?: string | null;
      }>;
    }>;
  },
): Promise<number> {
  const created = await apiFetch<{ id: number }>("/api/mrp/purchase-orders", {
    method: "POST",
    body: input,
  });
  navigate(`/mrp/purchase-orders/${created.id}`);
  return created.id;
}
